// src/posts/Megatron.jsx
// Scaling Training · Part 4 of 5 — Megatron-LM: Tensor Parallelism.
// TAGS FOR REGISTRATION: ['training', 'distributed', 'transformers']
// EXCERPT: Some transformer layers don't fit on a single GPU even after every other trick. Megatron-LM splits each matmul across GPUs: column-parallel for one matrix, row-parallel for the next, with one all-reduce stitching the partial sums back together. Two splits per block; that's the whole algorithm.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import usePageMeta from '../usePageMeta';
import './PostChrome.css';
import './Megatron.css';

/* ============================================================
   KaTeX wrapper
   ============================================================ */
function Katex({ tex, block = false, className = '' }) {
  const ref = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const render = () => {
      if (cancelled || !ref.current) return;
      if (window.katex) {
        try {
          window.katex.render(tex, ref.current, { displayMode: block, throwOnError: false });
        } catch {
          if (ref.current) ref.current.textContent = tex;
        }
      } else {
        setTimeout(render, 60);
      }
    };
    render();
    return () => { cancelled = true; };
  }, [tex, block]);
  return block
    ? <div ref={ref} className={`mg-katex-block ${className}`} />
    : <span ref={ref} className={`mg-katex-inline ${className}`} />;
}

/* ============================================================
   Widget — MLP block partitioning under tensor parallelism.
   Slider sets N (TP degree). Diagram shows:
     X · A_i (column-split) → GeLU → · B_i (row-split) → all-reduce → Z
   ============================================================ */
const GPU_COLORS = [
  'hsl(218, 70%, 50%)',
  'hsl(35, 85%, 55%)',
  'hsl(280, 50%, 55%)',
  'hsl(148, 55%, 42%)',
  'hsl(0, 65%, 55%)',
  'hsl(190, 70%, 45%)',
  'hsl(50, 90%, 50%)',
  'hsl(310, 50%, 55%)',
];

function MlpSplitWidget() {
  const N_OPTIONS = [1, 2, 4, 8];
  const [Nidx, setNidx] = useState(2); // default 4-way
  const N = N_OPTIONS[Nidx];

  const W = 760, H = 320;
  const padT = 30, padB = 50;
  const innerH = H - padT - padB;

  // Layout: five labeled stages laid out horizontally.
  //   X  →  A (col-split)  →  GeLU  →  B (row-split)  →  Z (all-reduce)
  // The wide matrices are 4× hidden width to evoke MLP up/down sizes.
  const slotW = 80;  // small tensor width
  const wideW = 130; // wide matrix (4·hidden) width
  const gap   = 26;
  const positions = (() => {
    let x = 24;
    const arr = [];
    const widths = [slotW, wideW, wideW, wideW, slotW]; // X, A, GeLU, B, Z
    for (let i = 0; i < 5; i++) {
      arr.push({ x, w: widths[i] });
      x += widths[i] + gap;
    }
    return arr;
  })();

  const yTop = padT;
  const tileH = innerH;

  // Helpers for drawing stripes
  const drawColSplit = (px, pw, py, ph) => {
    // Vertical stripes — one per GPU
    const stripeW = pw / N;
    return Array.from({ length: N }).map((_, i) => (
      <rect
        key={i}
        x={px + i * stripeW} y={py} width={stripeW} height={ph}
        fill={GPU_COLORS[i % GPU_COLORS.length]}
        stroke="white" strokeWidth="0.8"
      />
    ));
  };
  const drawRowSplit = (px, pw, py, ph) => {
    const stripeH = ph / N;
    return Array.from({ length: N }).map((_, i) => (
      <rect
        key={i}
        x={px} y={py + i * stripeH} width={pw} height={stripeH}
        fill={GPU_COLORS[i % GPU_COLORS.length]}
        stroke="white" strokeWidth="0.8"
      />
    ));
  };
  const drawFull = (px, pw, py, ph, fill) => (
    <rect x={px} y={py} width={pw} height={ph} fill={fill} stroke="var(--post-border)" />
  );

  const labels = [
    { i: 0, top: 'X', bot: <>[batch, seq, h]</> },
    { i: 1, top: 'A — up-proj', bot: <>[h, 4h] · col-split</> },
    { i: 2, top: 'GeLU', bot: <>[batch, seq, 4h] · col-split</> },
    { i: 3, top: 'B — down-proj', bot: <>[4h, h] · row-split</> },
    { i: 4, top: 'Z — all-reduce', bot: <>[batch, seq, h]</> },
  ];

  // KPIs
  const weightsPerGpu = (1 / N * 100).toFixed(0); // %
  const allReducesFwd = 1;
  const allReducesBwd = 1;

  return (
    <div className="viz-panel mg-widget">
      <div className="mg-controls">
        <label className="mg-slider-label">
          TP degree <span className="mg-val">N = {N}</span>
        </label>
        <div className="mg-stage-tabs">
          {N_OPTIONS.map((n, i) => (
            <button
              key={n}
              onClick={() => setNidx(i)}
              className={`mg-stage-btn ${Nidx === i ? 'is-active' : ''}`}
            >
              N = {n}
            </button>
          ))}
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="mg-svg" preserveAspectRatio="xMidYMid meet">
        {/* labels above each stage */}
        {labels.map((l) => (
          <g key={`top-${l.i}`}>
            <text x={positions[l.i].x + positions[l.i].w / 2} y={padT - 10}
                  textAnchor="middle" fontSize="12" fontWeight="600"
                  fill="var(--post-text)" fontFamily="var(--post-sans)">
              {l.top}
            </text>
          </g>
        ))}

        {/* tiles */}
        {/* X — full activation, single owner */}
        {drawFull(positions[0].x, positions[0].w, yTop, tileH, '#f1f5f9')}

        {/* A — column-split */}
        {drawColSplit(positions[1].x, positions[1].w, yTop, tileH)}

        {/* GeLU output — column-split (matches A's split) */}
        {drawColSplit(positions[2].x, positions[2].w, yTop, tileH)}

        {/* B — row-split */}
        {drawRowSplit(positions[3].x, positions[3].w, yTop, tileH)}

        {/* Z — replicated after all-reduce */}
        {drawFull(positions[4].x, positions[4].w, yTop, tileH, '#1e293b')}
        <text x={positions[4].x + positions[4].w / 2}
              y={yTop + tileH / 2 + 5} textAnchor="middle"
              fontSize="16" fontWeight="700" fill="#fff"
              fontFamily="var(--post-mono)">Σ</text>

        {/* arrows between */}
        {[0, 1, 2, 3].map((i) => {
          const x1 = positions[i].x + positions[i].w + 2;
          const x2 = positions[i + 1].x - 2;
          const y  = yTop + tileH / 2;
          const isAllReduce = i === 3;
          return (
            <g key={`arr-${i}`}>
              <line x1={x1} y1={y} x2={x2 - 8} y2={y}
                    stroke="var(--post-text-mut)" strokeWidth="1.4"
                    markerEnd="url(#mg-arrow)" />
              {isAllReduce && (
                <text x={(x1 + x2) / 2} y={y - 10} textAnchor="middle"
                      fontSize="10" fontFamily="var(--post-mono)"
                      fill="var(--post-accent)" fontWeight="600">
                  all-reduce
                </text>
              )}
            </g>
          );
        })}

        {/* bottom labels */}
        {labels.map((l) => (
          <text key={`bot-${l.i}`}
                x={positions[l.i].x + positions[l.i].w / 2}
                y={yTop + tileH + 16}
                textAnchor="middle" fontSize="10.5"
                fill="var(--post-text-mut)" fontFamily="var(--post-mono)">
            {l.bot}
          </text>
        ))}

        <defs>
          <marker id="mg-arrow" viewBox="0 0 10 10" refX="8" refY="5"
                  markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--post-text-mut)" />
          </marker>
        </defs>
      </svg>

      <div className="mg-kpis">
        <div className="mg-kpi">
          <div className="mg-kpi-label">Weights / GPU</div>
          <div className="mg-kpi-val">{weightsPerGpu}<span className="mg-kpi-tail">%</span></div>
          <div className="mg-kpi-sub">of the full MLP block (each GPU owns 1/N of A and B)</div>
        </div>
        <div className="mg-kpi">
          <div className="mg-kpi-label">All-reduce per MLP block</div>
          <div className="mg-kpi-val">{allReducesFwd + allReducesBwd}</div>
          <div className="mg-kpi-sub">
            1 forward (after B) + 1 backward (gradient) ·{' '}
            <strong>independent of N or hidden size</strong>
          </div>
        </div>
      </div>
      <p className="mg-caption">
        Vertical stripes = column-parallel split (one GPU per column of A);
        horizontal stripes = row-parallel split (one GPU per row of B). Each
        colour is a GPU. After the row-parallel matmul every GPU holds a
        partial sum; one all-reduce stitches them into the final{' '}
        <Katex tex="Z" />. The pattern is the same for the attention block
        — column-parallel on QKV, row-parallel on the output projection, one
        all-reduce.
      </p>
    </div>
  );
}

/* ============================================================
   Page
   ============================================================ */
export default function Megatron() {
  usePageMeta({
    title: 'Megatron-LM: Tensor Parallelism',
    description: 'How Megatron-LM splits each transformer matmul across GPUs — column-parallel for one matrix, row-parallel for the next, with one all-reduce stitching the partial sums back together. Two splits per block, intra-node only.',
    slug: 'megatron',
    publishedDate: '2026-06-25',
    keywords: ['Megatron', 'tensor parallel', 'model parallel', 'transformer', 'training'],
  });

  return (
    <article className="post-2026 mg-post">
      <div className="post-wrap">
        <div className="post-hero">
          <div className="post-series-tag">
            <span className="post-live-dot" aria-hidden="true" />Scaling Training · Part 4 of 5
          </div>
          <h1>Megatron-LM: Tensor Parallelism</h1>
          <p className="post-lede">
            <a className="post-link" href="/blog/zero">ZeRO</a> shards
            the parameters across data-parallel workers, but each worker
            still has to materialise a full layer at compute time. What
            if the layer itself doesn't fit? Megatron-LM splits the
            matmul. Two cuts per transformer block, one all-reduce, no
            magic.
          </p>
          <div className="post-byline">
            By <strong>Bernhard Walser</strong> &amp;{' '}
            <a className="post-link" href="https://www.anthropic.com/claude" target="_blank" rel="noreferrer"><strong>Claude</strong></a>
            {' '}(Anthropic) · co-written and co-designed ·{' '}
            <a className="post-link" href="https://www.linkedin.com/in/bernhardwalser/" target="_blank" rel="noreferrer">LinkedIn</a>
            {' · '}
            <a className="post-link" href="https://github.com/berniwal" target="_blank" rel="noreferrer">GitHub</a>
          </div>
        </div>

        <h2 className="reveal">When a single layer is the problem</h2>
        <p>
          A 100B+ parameter transformer has hidden dimensions in the tens
          of thousands. The MLP's intermediate dimension is typically{' '}
          <Katex tex="4 \times" /> larger again. Multiply by half-precision
          gradients and optimizer state and a <em>single block</em> blows
          past 40 GB on a current GPU — never mind the whole model. ZeRO
          can shard the storage all it wants; you still need to fit the
          live matmul somewhere when forward runs.
        </p>
        <p>
          <strong>Tensor parallelism</strong> attacks that problem
          directly. Rather than every GPU holding a full layer, each GPU
          holds a <em>slice</em> of every weight matrix and computes the
          matching slice of the output. The model never assembles
          itself in one place; the computation does.
        </p>

        <h2 className="reveal">The MLP block — column then row</h2>
        <p>
          A transformer MLP is two matrix multiplies with a non-linearity
          in between:
        </p>
        <Katex
          block
          tex={'Z \\;=\\; \\mathrm{GeLU}(X A)\\, B'}
        />
        <p>
          where <Katex tex="A" /> is the up-projection (<Katex tex="h \to 4h" />)
          and <Katex tex="B" /> is the down-projection
          (<Katex tex="4h \to h" />). Megatron splits these two matrices
          differently, and the choice matters.
        </p>
        <p>
          Split <Katex tex="A" /> <strong>column-wise</strong>:{' '}
          <Katex tex="A = [A_1, A_2, \ldots, A_N]" />. Then{' '}
          <Katex tex="X A_i" /> produces the <em>i</em>-th column block
          of <Katex tex="XA" />, and each GPU can compute its own slice
          locally. The reason this split — and not a row-wise split — is
          GeLU. GeLU is non-linear, so{' '}
          <Katex tex="\mathrm{GeLU}(a + b) \neq \mathrm{GeLU}(a) + \mathrm{GeLU}(b)" />.
          If you split <Katex tex="A" /> row-wise you'd have partial sums
          that need to be combined <em>before</em> the non-linearity. The
          column split avoids that all-reduce.
        </p>
        <p>
          Now split <Katex tex="B" /> <strong>row-wise</strong>:{' '}
          <Katex tex="B = [B_1; B_2; \ldots; B_N]" />. GPU <em>i</em>{' '}
          already holds the matching <em>i</em>-th column slice of{' '}
          <Katex tex="\mathrm{GeLU}(XA)" />, so it can locally compute{' '}
          <Katex tex="\mathrm{GeLU}(X A_i) \, B_i" /> — a partial sum of
          the final result. One all-reduce sums these partials across
          GPUs and you have <Katex tex="Z" />. The widget below makes
          the colour-coding concrete:
        </p>
        <MlpSplitWidget />

        <h2 className="reveal">The attention block — same trick</h2>
        <p>
          Attention partitions even more naturally: <strong>multi-head
          attention is already a column-split waiting to happen</strong>.
          The QKV projection produces tensors of shape{' '}
          <Katex tex="[\text{batch}, \text{seq}, \text{heads}, d_h]" />.
          Split column-wise along the head dimension and each GPU owns a
          subset of the heads. The softmax-and-weighted-sum that each
          head performs is entirely local — heads don't interact during
          attention. The output projection is row-parallel, just like
          the MLP's <Katex tex="B" />, and one all-reduce stitches the
          per-head outputs back together.
        </p>
        <p>
          So an attention block also costs one all-reduce per forward
          pass and one per backward pass. Combined with the MLP:{' '}
          <strong>four all-reduces per transformer block per training
          step</strong>. The volume of each is one activation tensor
          (<Katex tex="\text{batch} \times \text{seq} \times h" />),
          which is independent of <Katex tex="N" /> and independent of
          the hidden multiplier.
        </p>

        <h2 className="reveal">Why this only works intra-node</h2>
        <p>
          Four all-reduces per block sounds fine until you do the math
          on a 96-layer transformer:{' '}
          <Katex tex="96 \times 4 = 384" /> all-reduces per training
          step. Each one is small, but they're <strong>serial</strong> —
          you can't start the next layer until the current layer's
          all-reduce lands. The bottleneck is round-trip latency.
        </p>
        <p>
          Inside an 8-GPU node, NVLink offers ~300 GB/s and sub-microsecond
          latency. The all-reduces are essentially free; compute hides
          comm. Cross nodes, you're on InfiniBand at ~100 GB/s with much
          higher round-trip latency, and 384 round-trips per step starts
          to dominate. This is why Megatron is overwhelmingly used
          <strong> within a node</strong>, with{' '}
          <Katex tex="N \in \{2, 4, 8\}" />, and combined with data
          parallelism <em>across</em> nodes. The 3D-parallelism stack:
          tensor parallel within the node, ZeRO/data parallel between
          nodes, pipeline parallel layered on top if the model is huge.
        </p>

        <h2 className="reveal">Activations stay replicated — enter ZeRO-R</h2>
        <p>
          One subtle thing the diagram doesn't show: while the
          <em> weight</em> matrices are sharded, the <em>input
          activation</em> <Katex tex="X" /> is replicated on every TP
          worker, and so are the residual stream and LayerNorm outputs.
          That's necessary — every GPU needs the same{' '}
          <Katex tex="X" /> to compute its column slice of{' '}
          <Katex tex="XA_i" /> — but it eats memory.
        </p>
        <p>
          The same paper that introduced ZeRO came with a companion set
          of tricks called <strong>ZeRO-R</strong> (R for "residual")
          that target exactly this. <strong>Partitioned activation
          checkpointing</strong> shards the replicated activation
          checkpoints across the TP workers and all-gathers them on
          backward; an optional variant offloads the partitions to CPU
          memory. ZeRO-R is the natural complement to Megatron — model
          parallel turns weights into shards, ZeRO-R turns the
          accompanying activation duplication into shards.
        </p>

        <h2 className="reveal">Caveats and edge cases</h2>
        <p>
          <strong>Pre-LN is non-negotiable.</strong> The original BERT
          (Post-LN: normalize <em>after</em> the residual add) trains
          fine up to ~1B parameters and then diverges. Megatron found
          that Pre-LN (normalize <em>before</em> the sub-layer) was
          required to keep loss curves stable past that point. Modern
          architectures all use Pre-LN; if you encounter Post-LN code,
          it's legacy.
        </p>
        <p>
          <strong>Dropout RNG needs care.</strong> Dropout outside the
          TP region (e.g. embedding dropout) must use the <em>same</em>{' '}
          random mask across GPUs, otherwise replicas drift. Dropout
          inside the TP region (per-head attention dropout) must use{' '}
          <em>independent</em> masks per GPU — each GPU's slice is its
          own. Megatron seeds two separate generators.
        </p>
        <p>
          <strong>The output projection is a trap.</strong> The final
          linear-to-vocab and the loss together would naively require
          all-gathering a{' '}
          <Katex tex="\text{batch} \times \text{seq} \times \text{vocab}" />
          {' '}tensor — for a 50k vocab and modern sequence lengths
          that's hundreds of MB per step. Megatron fuses the output
          projection with the cross-entropy loss so only a scalar loss
          needs to be all-reduced. Worth knowing if you're rolling your
          own implementation.
        </p>

        <h2 className="reveal">References</h2>
        <div className="post-refs">
          <div className="head ref-cite">Cite</div>
          <div className="head ref-title">Title</div>
          <div className="head ref-link">Link</div>

          <div className="ref-cite">Shoeybi et al. 2019</div>
          <div>
            <div className="ref-title">
              <a href="https://arxiv.org/abs/1909.08053" target="_blank" rel="noreferrer">Megatron-LM: Training Multi-Billion Parameter Language Models Using Model Parallelism</a>
            </div>
            <div className="ref-note">The original paper. Column/row split for MLP and attention; Pre-LN; fused loss.</div>
          </div>
          <div className="ref-link"><a href="https://arxiv.org/abs/1909.08053" target="_blank" rel="noreferrer">arxiv.org/abs/1909.08053</a></div>

          <div className="ref-cite">Narayanan et al. 2021</div>
          <div>
            <div className="ref-title">
              <a href="https://arxiv.org/abs/2104.04473" target="_blank" rel="noreferrer">Efficient Large-Scale Language Model Training on GPU Clusters Using Megatron-LM</a>
            </div>
            <div className="ref-note">The follow-up — 3D parallelism (tensor + pipeline + data) on 3,072 GPUs, 1T-parameter scale.</div>
          </div>
          <div className="ref-link"><a href="https://arxiv.org/abs/2104.04473" target="_blank" rel="noreferrer">arxiv.org/abs/2104.04473</a></div>

          <div className="ref-cite">Earlier posts</div>
          <div>
            <div className="ref-title">
              <a href="/blog/zero">ZeRO: Sharding the Optimizer</a>
            </div>
            <div className="ref-note">The data-parallel companion. Combines with Megatron in the 3D stack.</div>
          </div>
          <div className="ref-link"><a href="/blog/zero">/zero</a></div>

          <div className="ref-cite"></div>
          <div>
            <div className="ref-title">
              <a href="/blog/ring-all-reduce">Ring All-Reduce</a>
            </div>
            <div className="ref-note">Where the per-all-reduce communication cost comes from.</div>
          </div>
          <div className="ref-link"><a href="/blog/ring-all-reduce">/ring-all-reduce</a></div>
        </div>

        <footer className="post-footer">
          <p>
            <strong>Part 4 of Scaling Training</strong> · Previous:{' '}
            <a className="post-link" href="/blog/ring-all-reduce">Ring All-Reduce</a>
            {' · '}Next:{' '}
            <a className="post-link" href="/blog/fsdp">PyTorch FSDP: ZeRO Inside PyTorch</a>.
          </p>
          <p style={{ marginBottom: 0 }}>
            Bernhard Walser · ML Engineer, Digitec Galaxus · ETH Computer Science ·{' '}
            <a className="post-link" href="https://www.linkedin.com/in/bernhardwalser/" target="_blank" rel="noreferrer">LinkedIn</a>
            {' · '}
            <a className="post-link" href="https://github.com/berniwal" target="_blank" rel="noreferrer">GitHub</a>
          </p>
        </footer>
      </div>
    </article>
  );
}
